import * as React from 'react';
import { type LucideIcon } from 'lucide-react';

import { appIdentity } from '@/app/app-config';
import { Breadcrumbs } from '@/components/layout/breadcrumbs';

/**
 * Full-bleed page title banner used at the top of every page.
 *
 * Renders a light-grey (muted) background strip that bleeds to the edges of
 * the content area via negative margin, matching the parent <main> padding
 * (p-4 / md:p-6). The page title is large and prominent; an optional
 * description, icon, and action slot are preserved.
 *
 * Also keeps the document <title> in sync (WCAG 2.4.2).
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  actions
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  actions?: React.ReactNode;
}) {
  React.useEffect(() => {
    document.title = `${title} · ${appIdentity.name}`;
    return () => {
      document.title = appIdentity.name;
    };
  }, [title]);

  return (
    /*
     * Negative margin lets the banner break out of <main>'s padding so the
     * background spans edge-to-edge. The inner div re-applies the same
     * horizontal padding so text/actions stay aligned with page content.
     */
    <div className="bg-muted/60 border-border -mx-4 -mt-4 mb-2 border-b px-4 py-5 md:-mx-6 md:-mt-6 md:px-6 md:py-6">
      <div className="mb-2"><Breadcrumbs /></div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="bg-background text-foreground flex size-10 shrink-0 items-center justify-center rounded-md border shadow-sm">
              <Icon className="size-5" aria-hidden />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
            {description && (
              <p className="text-muted-foreground mt-0.5 text-sm leading-snug">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
